import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Business,
  Home,
  Payment,
  Report,
  Build,
  Notifications,
  AccountCircle,
  Logout,
  QrCodeScanner,
  QrCode2,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import myhomeLogo from '../assests/myhome-logo.png';

const drawerWidth = 240;

const Layout = () => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Menu items with translation keys
  const adminMenuItems = [
    { text: t('layout.navigation.dashboard'), icon: <Dashboard />, path: '/' },
    { text: t('layout.navigation.users'), icon: <People />, path: '/users' },
    { text: t('layout.navigation.buildings'), icon: <Business />, path: '/buildings' },
    { text: t('layout.navigation.payments'), icon: <Payment />, path: '/payments' },
    { text: t('layout.navigation.notifications'), icon: <Notifications />, path: '/notifications' },
  ];

  const buildingMenuItems = [
    { text: t('layout.navigation.dashboard'), icon: <Dashboard />, path: '/' },
    { text: t('layout.navigation.visitors'), icon: <Build />, path: '/visitors' },
    { text: t('layout.navigation.facilitiesBooking'), icon: <Build />, path: '/facility-booking' },
    { text: t('layout.navigation.expenses'), icon: <Build />, path: '/expenses' },
    { text: t('layout.navigation.qrGenerator'), icon: <QrCode2 />, path: '/qr-generator' },
    { text: t('layout.navigation.massNotifications'), icon: <Notifications />, path: '/mass-notifications' },
    { text: t('layout.navigation.EmployeeDetails'), icon: <People />, path: '/employee-details' }
  ];

  const maintenanceStaffMenuItems = [
    { text: t('layout.navigation.visitors'), icon: <Build />, path: '/visitors' },
    { text: t('layout.navigation.vehicleSecurity'), icon: <QrCodeScanner />, path: '/vehicle-security' },
  ];

  const ownerTenantMenuItems = [
    { text: t('layout.navigation.dashboard'), icon: <Dashboard />, path: '/' },
    { text: t('layout.navigation.visitors'), icon: <Build />, path: '/visitors' },
    { text: t('layout.navigation.facilitiesBooking'), icon: <Build />, path: '/facility-booking' },
    { text: t('layout.navigation.EmployeeDetails'), icon: <People />, path: '/employee-details' },
  ];

  // Determine which menu items to show based on user groups
  const groups = user?.groups || [];
  
  // Extract group names - handle both old format (strings) and new format (objects)
  const groupNames = groups.map(g => typeof g === 'string' ? g : g.name);
  
  // Check user roles - also check the direct role property
  const userRole = user?.role;
  const isAdminOrStaff = groupNames.some(g => ['admin'].includes(g)) || userRole === 'super_admin';
  const isOwnerOrTenant = groupNames.some(g => ['owner', 'tenant'].includes(g)) || userRole === 'resident';
  const isBuildingAdmin = groupNames.some(g => ['building_admin'].includes(g)) || userRole === 'building_admin';
  const isMaintenanceStaff = groupNames.some(g => ['maintenance_staff'].includes(g)) || userRole === 'maintenance_staff';
  
  const menuItems = isAdminOrStaff ? adminMenuItems : 
                   isOwnerOrTenant ? ownerTenantMenuItems :
                   isBuildingAdmin ? buildingMenuItems : 
                   isMaintenanceStaff ? maintenanceStaffMenuItems : 
                   buildingMenuItems;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Close mobile drawer when navigating on mobile
  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false); // Always close drawer after navigation
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  // Close mobile drawer when location changes (for browser navigation)
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close drawer when resizing from mobile to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 600) { // sm breakpoint
        setMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const drawer = (
    <div>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
          <img
            src={myhomeLogo}
            alt={t('layout.logo.alt')}
            style={{
              height: '50px',
              width: 'auto',
              maxWidth: '90%',
              objectFit: 'contain',
              margin: 'auto',
            }}
          />
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ 
      display: 'flex',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box'
    }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            {isOwnerOrTenant
              ? t('layout.dashboards.ownerTenant')
              : isBuildingAdmin
                ? t('layout.dashboards.buildingAdmin')
                : isMaintenanceStaff
                  ? t('layout.dashboards.maintenanceStaff')
                  : (menuItems.find(item => item.path === location.pathname)?.text || t('layout.navigation.dashboard'))}
          </Typography>
          
          {/* Notification Bell - Show for all logged-in users */}
          <NotificationBell />
          
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.firstName ? user.firstName.charAt(0).toUpperCase() : <AccountCircle />}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              // Add smooth transition for better UX
              transition: 'transform 0.225s cubic-bezier(0, 0, 0.2, 1)',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          // Ensure proper spacing and responsive behavior
          mt: { xs: '64px', sm: 0 }, // Account for mobile AppBar height
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
        className="main-content-container"
      >
        <Toolbar sx={{ display: { xs: 'none', sm: 'block' } }} /> {/* Desktop toolbar spacer */}
        <Outlet />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { handleNavigation('/profile'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          {t('layout.profile.profile')}
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {t('layout.profile.logout')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;